import React, { useMemo } from "react";
import {
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  PieChart, Pie, Cell,
  ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";

// Chart series colors — semantic palette, works on both light/dark backgrounds
const DEFAULT_COLORS = [
  "var(--feedback-info)", "#10b981", "var(--feedback-warning)", "var(--feedback-error)", "#8b5cf6",
  "#06b6d4", "#84cc16", "#f97316", "#ec4899", "var(--text-muted)",
];

function resolveColor(series, i) {
  return series?.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
}

function axisStyle(scale = 1) {
  return { fontSize: Math.max(9, Math.round(11 * scale)), fill: "var(--text-muted)", fontFamily: "Inter, sans-serif" };
}

function tooltipStyle(scale = 1) {
  return {
    backgroundColor: "var(--surface-base)",
    border: "1px solid var(--border-default)",
    borderRadius: 8,
    fontSize: Math.max(10, Math.round(12 * scale)),
    fontFamily: "Inter, sans-serif",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    color: "var(--text-primary)",
  };
}

function ChartTable({ data, series, xAxis, scale = 1 }) {
  if (!data?.length) return <EmptyState />;
  const cols = [
    ...(xAxis?.key ? [{ key: xAxis.key, label: xAxis.label || xAxis.key }] : []),
    ...(series || []).map((s) => ({ key: s.key, label: s.label || s.key })),
  ];
  // The Traces table (dataTableStyles), scaled with the card.
  const fs = Math.max(10, Math.round(12 * scale));
  const fsH = Math.max(9, Math.round(10.5 * scale));
  const rowH = Math.round(32 * scale);
  return (
    <div
      className="rounded-md border border-[color:var(--border-default)] bg-surface"
      style={{ overflowX: "auto", overflowY: "auto", maxHeight: "100%", fontSize: fs }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Inter, sans-serif" }}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.key}
                style={{
                  height: rowH,
                  padding: "0 12px",
                  textAlign: "left",
                  color: "var(--text-muted)",
                  fontWeight: 600,
                  fontSize: fsH,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  whiteSpace: "nowrap",
                  // Sticky: the card surface (no header fill) so rows don't show
                  // through; the hairline is a shadow so it sticks with the header.
                  backgroundColor: "var(--surface-base)",
                  boxShadow: "inset 0 -1px 0 var(--border-default)",
                  position: "sticky",
                  top: 0,
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr key={ri} className="border-b border-[color:var(--border-subtle)] last:border-b-0" style={{ height: rowH }}>
              {cols.map((c) => (
                <td
                  key={c.key}
                  style={{
                    padding: "0 12px",
                    verticalAlign: "middle",
                    color: "var(--text-primary)",
                    fontSize: fs,
                  }}
                >
                  {row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: "var(--text-muted)",
        fontSize: 13,
        fontFamily: "Inter, sans-serif",
      }}
    >
      No data
    </div>
  );
}

export default function CustomChartCard({ card, contentScale = 1 }) {
  const cfg = useMemo(() => {
    const q = card?.queryDsl;
    if (!q || typeof q !== "object") return null;
    return q;
  }, [card]);

  if (!cfg) return <EmptyState />;

  const {
    chartType = "bar",
    data = [],
    xAxis,
    series = [],
    config = {},
  } = cfg;

  const showLegend = config.showLegend !== false;
  const showGrid = config.showGrid !== false;

  if (!data.length) return <EmptyState />;

  const xKey = xAxis?.key || (data[0] ? Object.keys(data[0])[0] : "x");

  const aStyle = axisStyle(contentScale);
  const tStyle = tooltipStyle(contentScale);
  const legendFs = Math.max(9, Math.round(11 * contentScale));

  const gridEl = showGrid ? <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" /> : null;
  const legendEl = showLegend && series.length > 1 ? (
    <Legend wrapperStyle={{ fontSize: legendFs, fontFamily: "Inter, sans-serif", color: "var(--text-secondary)" }} />
  ) : null;
  const xAxisEl = (
    <XAxis dataKey={xKey} tick={aStyle} axisLine={false} tickLine={false} label={xAxis?.label ? { value: xAxis.label, position: "insideBottom", offset: -4, style: aStyle } : undefined} />
  );
  const yAxisEl = (
    <YAxis tick={aStyle} axisLine={false} tickLine={false} width={38} />
  );
  // Cursor uses a subtle brand tint — readable on both light/dark backgrounds
  const tooltipEl = <Tooltip contentStyle={tStyle} cursor={{ fill: "var(--surface-hover)" }} />;

  if (chartType === "table") {
    return <ChartTable data={data} series={series} xAxis={xAxis} scale={contentScale} />;
  }

  if (chartType === "pie") {
    const valueKey = series[0]?.key || (data[0] ? Object.keys(data[0]).find((k) => k !== xKey) : "value");
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey={valueKey}
            nameKey={xKey}
            cx="50%"
            cy="50%"
            outerRadius="70%"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            labelLine={false}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip contentStyle={tStyle} />
          {showLegend && <Legend wrapperStyle={{ fontSize: legendFs, fontFamily: "Inter, sans-serif" }} />}
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "scatter") {
    const xS = series[0]?.key || xKey;
    const yS = series[1]?.key || (series[0]?.key !== xKey ? series[0]?.key : undefined);
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart>
          {gridEl}
          <XAxis dataKey={xS} type="number" tick={aStyle} axisLine={false} tickLine={false} name={xAxis?.label || xS} />
          <YAxis dataKey={yS} type="number" tick={aStyle} axisLine={false} tickLine={false} width={38} />
          <Tooltip contentStyle={tStyle} cursor={{ strokeDasharray: "3 3" }} />
          {legendEl}
          <Scatter data={data} fill={DEFAULT_COLORS[0]} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "line") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          {gridEl}
          {xAxisEl}
          {yAxisEl}
          {tooltipEl}
          {legendEl}
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label || s.key}
              stroke={resolveColor(s, i)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "area") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          {gridEl}
          {xAxisEl}
          {yAxisEl}
          {tooltipEl}
          {legendEl}
          {series.map((s, i) => {
            const color = resolveColor(s, i);
            return (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label || s.key}
                stroke={color}
                fill={color}
                fillOpacity={0.12}
                strokeWidth={2}
                dot={false}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // Default: bar
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
        {gridEl}
        {xAxisEl}
        {yAxisEl}
        {tooltipEl}
        {legendEl}
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label || s.key}
            fill={resolveColor(s, i)}
            radius={[3, 3, 0, 0]}
            maxBarSize={48}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
